import { unwrapSyncApiEnvelope } from './syncApiEnvelope.ts';

function asObjectArray(v: unknown): Record<string, unknown>[] {
  return Array.isArray(v) ? v.filter((x) => x && typeof x === 'object' && !Array.isArray(x)) as Record<string, unknown>[] : [];
}

function normalizePublicId(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

function pickPublicId(r: Record<string, unknown>): string {
  return normalizePublicId(r.publicId ?? r.public_id);
}

function readNombre(r: Record<string, unknown>): string {
  const v = r.nombre ?? r.name ?? r.descripcion;
  return typeof v === 'string' ? v.trim() : '';
}

function readStringIdArray(r: Record<string, unknown>, camelKey: string, snakeKey: string): string[] | null {
  if (!(camelKey in r) && !(snakeKey in r)) return null;
  const v = r[camelKey] ?? r[snakeKey];
  if (!Array.isArray(v)) return [];
  return v.map((x) => normalizePublicId(x)).filter(Boolean);
}

function appendCatalogRows(existing: Record<string, unknown>[], rows: Record<string, unknown>[]): Record<string, unknown>[] {
  return existing.length === 0 ? [...rows] : [...existing, ...rows];
}

function aliasCatalogSection(inner: Record<string, unknown>, fromKey: string, toKey: string): void {
  const rows = asObjectArray(inner[fromKey]);
  if (rows.length === 0) return;
  inner[toKey] = appendCatalogRows(asObjectArray(inner[toKey]), rows);
  delete inner[fromKey];
}

function linkProductoReposicionToItem(row: Record<string, unknown>, itemPublicId: string): void {
  const itemId = normalizePublicId(itemPublicId);
  if (!itemId) return;
  const linked = readStringIdArray(row, 'itemsPublicIds', 'items_public_ids') ?? [];
  if (!linked.includes(itemId)) row.itemsPublicIds = [...linked, itemId];
}

function mergeProductoReposicionRow(
  byId: Map<string, Record<string, unknown>>,
  row: Record<string, unknown>,
  itemPublicId = '',
): void {
  const publicId = pickPublicId(row);
  if (!publicId) return;
  let product = byId.get(publicId);
  if (!product) {
    product = { ...row, publicId, nombre: readNombre(row) };
    byId.set(publicId, product);
  } else if (!readNombre(product) && readNombre(row)) {
    product.nombre = readNombre(row);
  }
  const ids = readStringIdArray(product, 'itemsPublicIds', 'items_public_ids');
  if (ids !== null) product.itemsPublicIds = ids;
  linkProductoReposicionToItem(product, itemPublicId);
}

function backfillProductosReposicionFromEntrega(
  out: Record<string, unknown>,
  productosById: Map<string, Record<string, unknown>>,
): void {
  if (productosById.size > 0) return;
  for (const row of asObjectArray(out.productosEntrega)) {
    mergeProductoReposicionRow(productosById, row);
  }
}

function assignProductosReposicion(
  out: Record<string, unknown>,
  productosById: Map<string, Record<string, unknown>>,
): void {
  backfillProductosReposicionFromEntrega(out, productosById);
  if (productosById.size > 0) out.productosReposicion = [...productosById.values()];
}

/** Inclusion Social envelope: `{ status, code, mensaje, data }`. Throws on auth/server errors. */
export function parseCatalogPullEnvelope(raw: unknown): Record<string, unknown> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error('No se pudieron descargar catálogos: respuesta inválida del servidor.');
  }
  const record = raw as Record<string, unknown>;
  if (record.status === false) {
    const msg =
      typeof record.mensaje === 'string' && record.mensaje.trim()
        ? record.mensaje.trim()
        : 'No se pudieron descargar catálogos.';
    throw new Error(msg);
  }
  const code = record.code;
  if (typeof code === 'number' && code >= 400) {
    const msg =
      typeof record.mensaje === 'string' && record.mensaje.trim()
        ? record.mensaje.trim()
        : `No se pudieron descargar catálogos (código ${code}).`;
    throw new Error(msg);
  }
  return unwrapSyncApiEnvelope(raw);
}

/** Flatten nested pull sections for local merge; maps `alimentos` aliases onto `productosReposicion`. */
export function normalizeCatalogPullSections(inner: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...inner };
  aliasCatalogSection(out, 'alimentos', 'productosReposicion');
  aliasCatalogSection(out, 'productos_reposicion', 'productosReposicion');

  const productosById = new Map<string, Record<string, unknown>>();
  for (const existing of asObjectArray(out.productosReposicion)) {
    mergeProductoReposicionRow(productosById, existing);
  }

  const itemsEntrega = asObjectArray(out.itemsEntrega);
  if (itemsEntrega.length === 0) {
    assignProductosReposicion(out, productosById);
    return out;
  }

  const flatItems: Record<string, unknown>[] = [];
  const ciclosById = new Map<string, Record<string, unknown>>();
  for (const existing of asObjectArray(out.ciclos)) {
    const id = pickPublicId(existing);
    if (id) ciclosById.set(id, existing);
  }

  for (const item of itemsEntrega) {
    const itemPublicId = pickPublicId(item);
    const nestedCiclos = asObjectArray(item.ciclos);
    const nestedAlimentos = asObjectArray(item.alimentos);
    const nestedProductos = asObjectArray(item.productosReposicion);
    const { ciclos: _c, alimentos: _a, productosReposicion: _p, ...rest } = item;
    flatItems.push(rest);

    nestedCiclos.forEach((ciclo, idx) => {
      const cicloId = pickPublicId(ciclo);
      if (!cicloId) return;
      ciclosById.set(cicloId, {
        publicId: cicloId,
        nombre: typeof ciclo.nombre === 'string' ? ciclo.nombre : '',
        numero: idx + 1,
        fecha_inicio: ciclo.fecha_inicio ?? ciclo.fechaInicio ?? '',
        fecha_fin: ciclo.fecha_fin ?? ciclo.fechaFin ?? '',
        itemEntregaPublicId: itemPublicId,
      });
    });

    for (const row of [...nestedAlimentos, ...nestedProductos]) {
      mergeProductoReposicionRow(productosById, row, itemPublicId);
    }
  }

  out.itemsEntrega = flatItems;
  if (ciclosById.size > 0) out.ciclos = [...ciclosById.values()];
  assignProductosReposicion(out, productosById);
  return out;
}
